// Rule-set administration (0023): shown on the Rules page only when the server says the
// signed-in person holds a current administer_policy grant (PolicyView.can_administer).
// Every step is a server command; the server re-checks the grant, the lifecycle, the
// evidence and the capability check, so this panel only gathers input and shows blockers.
//
// The review/approval form is never prefilled: the reviewer or approver is named exactly
// as the signed document states, and the rules fingerprint is pasted from it. The person
// recording is the signed-in administrator, kept separately by the server.
import { useQuery } from '@tanstack/react-query'
import { useId, useState } from 'react'
import { DateInput, DateTimeInput } from '@/components/DateTimeInput'
import { BlockerList, ErrorBanner } from '@/components/evidence'
import { Badge, Banner, Button, Card, Checkbox, Field, SectionTitle, Select, TextArea, TextInput } from '@/components/ui'
import {
  ACTIVATION_DECLARATION,
  APPROVAL_RECORD_DECLARATION,
  approvalFormErrors,
  type ApprovalForm,
  type ApprovalKind,
  emptyApprovalForm,
  FREEZE_DECLARATION,
  WITHDRAWAL_DECLARATION,
} from '@/domain/policyActivation'
import type { CommandName, PolicyAdminView } from '@/domain/types'
import { callRead } from '@/lib/commands'
import { fmtDateTime, fromLocalInput, shortDigest } from '@/lib/format'
import { useCommand, useMe } from '@/lib/queries'
import { type AdminVersion, adminActions, lifecycleInfo, parseValueInput, versionLifecycle } from './policyModel'

function usePolicyAdmin() {
  const me = useMe()
  const account = me.data?.account_id ?? null
  return useQuery({
    queryKey: ['policy-admin', account],
    queryFn: () => callRead<PolicyAdminView>('ptw_policy_admin'),
    enabled: account !== null,
    staleTime: 30_000,
  })
}

export function PolicyAdmin({ onView, viewingId }: { onView: (id: string | null) => void; viewingId: string | null }) {
  const q = usePolicyAdmin()
  if (q.isLoading) return null
  if (!q.data) return <ErrorBanner error={q.error ?? new Error('Not available')} title="Rule-set administration is not available" />
  const view = q.data
  const current = view.versions.find((v) => v.id === viewingId) ?? null
  return (
    <Card>
      <SectionTitle aside={<Badge tone="brand">administer_policy</Badge>}>Rule-set administration</SectionTitle>
      <p className="text-sm text-ink-soft">
        You can prepare, freeze, record the review and approval of, activate and withdraw rule versions. Recording an approval does not
        make you an approver: name the person who signed. Environment: <span className="font-semibold">{view.environment_kind}</span>.
      </p>
      <ul className="mt-3 divide-y divide-line">
        {view.versions.map((v) => {
          const life = lifecycleInfo(versionLifecycle({ version: v, lifecycle: v.lifecycle }))
          return (
            <li key={v.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
              <span className="text-sm text-ink">
                <span className="code font-semibold">{v.version_label}</span> <span className="text-ink-soft">{v.profile_code}</span>{' '}
                <Badge tone={life.tone}>{life.label}</Badge>{' '}
                <span className="text-xs text-ink-soft">
                  {v.parameter_count} values, {v.unresolved_count} unresolved
                </span>
              </span>
              <Button variant={v.id === viewingId ? 'secondary' : 'ghost'} onClick={() => onView(v.id)} disabled={v.id === viewingId}>
                {v.id === viewingId ? 'Shown below' : 'Show'}
              </Button>
            </li>
          )
        })}
      </ul>
      <CreateCandidate versions={view.versions} onCreated={onView} />
      {current ? <VersionActions v={current} view={view} /> : null}
    </Card>
  )
}

function CommandResultBanner({ error }: { error: unknown }) {
  if (!error) return null
  const blockers = (error as { blockers?: unknown[] }).blockers
  if (Array.isArray(blockers) && blockers.length > 0) {
    return (
      <div className="mt-2">
        <BlockerList blockers={blockers as Parameters<typeof BlockerList>[0]['blockers']} />
      </div>
    )
  }
  return <ErrorBanner error={error} title="Not recorded" />
}

function CreateCandidate({ versions, onCreated }: { versions: AdminVersion[]; onCreated: (id: string) => void }) {
  const cmd = useCommand('create_policy_candidate')
  const [label, setLabel] = useState('')
  const [base, setBase] = useState('')
  const labelId = useId()
  const baseId = useId()
  const bases = versions.filter((v) => !v.profile_is_synthetic)
  return (
    <div className="mt-4 rounded-lg border border-line p-3">
      <p className="text-sm font-semibold text-ink">New candidate</p>
      <p className="mt-0.5 text-xs text-ink-soft">
        From the BadrEOR operational skeleton (explicit RS duties and OM approval, every other value unresolved) or as a copy of a real
        version. A synthetic version is never copied.
      </p>
      <div className="mt-2 grid gap-2 sm:grid-cols-2">
        <Field label="Version label" htmlFor={labelId} required>
          <TextInput id={labelId} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="for example BEOR-1.0" />
        </Field>
        <Field label="Start from" htmlFor={baseId}>
          <Select
            id={baseId}
            value={base}
            onChange={(e) => setBase(e.target.value)}
            placeholder="BadrEOR operational skeleton"
            options={bases.map((v) => ({ value: v.id, label: `Copy of ${v.version_label}` }))}
          />
        </Field>
      </div>
      <Button
        loading={cmd.isPending}
        disabled={!label.trim()}
        onClick={async () => {
          const r = await cmd.run(base ? { base_version_id: base, version_label: label.trim() } : { template: 'beor_operational', version_label: label.trim() }).catch(() => null)
          if (r && typeof r.policy_version_id === 'string') onCreated(r.policy_version_id)
        }}
      >
        Create candidate
      </Button>
      <CommandResultBanner error={cmd.error} />
    </div>
  )
}

function VersionActions({ v, view }: { v: AdminVersion; view: PolicyAdminView }) {
  const { available, notes } = adminActions(v, view.environment_kind)
  return (
    <div className="mt-4 space-y-3">
      <p className="text-sm font-semibold text-ink">
        Version <span className="code">{v.version_label}</span>
      </p>
      {notes.map((n) => (
        <Banner key={n} tone="info">
          {n}
        </Banner>
      ))}
      {v.activation_blockers.length > 0 && !v.profile_is_synthetic ? (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-ink-faint">What stops activation now</p>
          <BlockerList blockers={v.activation_blockers} />
        </div>
      ) : null}
      {available.includes('edit') ? <EditValue v={v} /> : null}
      {available.includes('freeze') ? (
        <Declared name="freeze_policy_candidate" versionId={v.id} title="Freeze for review and approval" declaration={FREEZE_DECLARATION} />
      ) : null}
      {available.includes('record_review') ? <RecordApproval v={v} kind="review" /> : null}
      {available.includes('record_approval') ? <RecordApproval v={v} kind="approval" /> : null}
      {available.includes('activate') ? <Activate v={v} view={view} /> : null}
      {available.includes('withdraw') ? (
        <Declared name="withdraw_policy_version" versionId={v.id} title="Withdraw this version" declaration={WITHDRAWAL_DECLARATION} needsReason danger />
      ) : null}
    </div>
  )
}

function EditValue({ v }: { v: AdminVersion }) {
  const cmd = useCommand('set_policy_candidate_value')
  const [code, setCode] = useState('')
  const [value, setValue] = useState('')
  const [status, setStatus] = useState<'approved' | 'source_confirmed_pending_approval' | 'unresolved'>('unresolved')
  const [source, setSource] = useState('')
  const [note, setNote] = useState('')
  const ids = { code: useId(), value: useId(), status: useId(), source: useId(), note: useId() }
  return (
    <div className="rounded-lg border border-line p-3">
      <p className="text-sm font-semibold text-ink">Set a value</p>
      <Field label="Parameter code" htmlFor={ids.code} required>
        <TextInput id={ids.code} value={code} onChange={(e) => setCode(e.target.value)} placeholder="for example class_b.notice_min_hours" />
      </Field>
      <Field label="Status" htmlFor={ids.status} required>
        <Select
          id={ids.status}
          value={status}
          onChange={(e) => setStatus(e.target.value as typeof status)}
          options={[
            { value: 'approved', label: 'Approved source recorded (adopted when this version is approved)' },
            { value: 'source_confirmed_pending_approval', label: 'Source confirmed, pending approval (blocks a built route)' },
            { value: 'unresolved', label: 'Unresolved (no value; its step stays blocked)' },
          ]}
        />
      </Field>
      {status !== 'unresolved' ? (
        <Field label="Value (JSON or text)" htmlFor={ids.value} required>
          <TextArea id={ids.value} value={value} onChange={(e) => setValue(e.target.value)} />
        </Field>
      ) : null}
      {status === 'approved' ? (
        <Field label="Approved source (document, revision, clause)" htmlFor={ids.source} required>
          <TextInput id={ids.source} value={source} onChange={(e) => setSource(e.target.value)} />
        </Field>
      ) : null}
      <Field label="Note" htmlFor={ids.note}>
        <TextInput id={ids.note} value={note} onChange={(e) => setNote(e.target.value)} />
      </Field>
      <Button
        loading={cmd.isPending}
        disabled={!code.trim() || (status !== 'unresolved' && !value.trim()) || (status === 'approved' && !source.trim())}
        onClick={() =>
          void cmd
            .run({
              policy_version_id: v.id,
              parameter_code: code.trim(),
              value: status === 'unresolved' ? null : parseValueInput(value),
              value_status: status,
              source_reference: status === 'approved' ? source.trim() : null,
              note: note.trim() || null,
            })
            .catch(() => null)
        }
      >
        Save value
      </Button>
      <CommandResultBanner error={cmd.error} />
    </div>
  )
}

function Declared({ name, versionId, title, declaration, needsReason, danger }: {
  name: Extract<CommandName, 'freeze_policy_candidate' | 'withdraw_policy_version'>
  versionId: string
  title: string
  declaration: string
  needsReason?: boolean
  danger?: boolean
}) {
  const cmd = useCommand(name)
  const [confirmed, setConfirmed] = useState(false)
  const [reason, setReason] = useState('')
  const reasonId = useId()
  return (
    <div className="rounded-lg border border-line p-3">
      <p className="text-sm font-semibold text-ink">{title}</p>
      {needsReason ? (
        <Field label="Reason" htmlFor={reasonId} required>
          <TextInput id={reasonId} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
      ) : null}
      <Checkbox checked={confirmed} onChange={setConfirmed} label={declaration} />
      <Button
        variant={danger ? 'danger' : 'primary'}
        loading={cmd.isPending}
        disabled={!confirmed || (needsReason && !reason.trim())}
        onClick={() =>
          void (name === 'withdraw_policy_version'
            ? cmd.run({ policy_version_id: versionId, reason: reason.trim(), declaration_confirmed: confirmed } as never)
            : cmd.run({ policy_version_id: versionId, declaration_confirmed: confirmed } as never)
          ).catch(() => null)
        }
      >
        {title}
      </Button>
      <CommandResultBanner error={cmd.error} />
    </div>
  )
}

function RecordApproval({ v, kind }: { v: AdminVersion; kind: ApprovalKind }) {
  const cmd = useCommand('record_policy_approval')
  const [form, setForm] = useState<ApprovalForm>(() => emptyApprovalForm(kind))
  const [digest, setDigest] = useState('')
  const [tried, setTried] = useState(false)
  const today = new Date().toISOString().slice(0, 10)
  const errors = approvalFormErrors(form, today)
  const digestError = digest.trim() && v.freeze && digest.trim() !== v.freeze.rules_digest ? 'This is not the frozen fingerprint.' : !digest.trim() ? 'Paste the fingerprint printed on the signed document.' : null
  const set = <K extends keyof ApprovalForm>(k: K, value: ApprovalForm[K]) => setForm((f) => ({ ...f, [k]: value }))
  const id = { name: useId(), title: useId(), on: useId(), doc: useId(), rev: useId(), eff: useId(), loc: useId(), notes: useId(), digest: useId() }
  const show = (k: keyof ApprovalForm) => (tried ? errors[k] : undefined)
  const who = kind === 'review' ? 'Reviewer' : 'Approver'
  return (
    <div className="rounded-lg border border-line p-3">
      <p className="text-sm font-semibold text-ink">{kind === 'review' ? 'Record the review' : 'Record the approval'}</p>
      <p className="mt-0.5 text-xs text-ink-soft">
        Enter what the signed document says. {kind === 'approval' ? 'The approver must be a different person from the reviewer. ' : ''}
        Frozen fingerprint: <span className="code">{shortDigest(v.freeze?.rules_digest)}</span>.
      </p>
      <div className="mt-2 grid gap-2 sm:grid-cols-2">
        <Field label={`${who} name`} htmlFor={id.name} required error={show('approver_name')}>
          <TextInput id={id.name} value={form.approver_name} onChange={(e) => set('approver_name', e.target.value)} autoComplete="off" />
        </Field>
        <Field label={`${who} position`} htmlFor={id.title} required error={show('approver_role_title')} hint="As the document states it, for example HSE Manager or CEO.">
          <TextInput id={id.title} value={form.approver_role_title} onChange={(e) => set('approver_role_title', e.target.value)} autoComplete="off" />
        </Field>
        <Field label="Date of the decision" htmlFor={id.on} required error={show('approved_on')}>
          <DateInput id={id.on} value={form.approved_on} onChange={(x) => set('approved_on', x)} />
        </Field>
        {kind === 'approval' ? (
          <Field label="Effective from (as approved)" htmlFor={id.eff} required error={show('effective_from')}>
            <DateTimeInput id={id.eff} value={form.effective_from} onChange={(x) => set('effective_from', x)} />
          </Field>
        ) : null}
        <Field label="Document reference" htmlFor={id.doc} required error={show('document_reference')}>
          <TextInput id={id.doc} value={form.document_reference} onChange={(e) => set('document_reference', e.target.value)} />
        </Field>
        <Field label="Document revision" htmlFor={id.rev} required error={show('document_revision')}>
          <TextInput id={id.rev} value={form.document_revision} onChange={(e) => set('document_revision', e.target.value)} />
        </Field>
        <Field label="Where the signed copy is kept" htmlFor={id.loc}>
          <TextInput id={id.loc} value={form.evidence_location} onChange={(e) => set('evidence_location', e.target.value)} />
        </Field>
        <Field label="Rules fingerprint on the signed document" htmlFor={id.digest} required error={tried ? digestError : undefined}>
          <TextInput id={id.digest} value={digest} onChange={(e) => setDigest(e.target.value)} className="code" />
        </Field>
      </div>
      <Field label="Notes" htmlFor={id.notes}>
        <TextInput id={id.notes} value={form.notes} onChange={(e) => set('notes', e.target.value)} />
      </Field>
      <Checkbox checked={form.declaration_confirmed} onChange={(x) => set('declaration_confirmed', x)} label={APPROVAL_RECORD_DECLARATION} />
      {tried && errors.declaration_confirmed ? <p className="text-[13px] font-semibold text-bad">{errors.declaration_confirmed}</p> : null}
      <Button
        loading={cmd.isPending}
        onClick={() => {
          setTried(true)
          if (Object.keys(errors).length > 0 || digestError) return
          void cmd
            .run({
              policy_version_id: v.id,
              approval_kind: kind,
              approver_name: form.approver_name.trim(),
              approver_role_title: form.approver_role_title.trim(),
              approved_on: form.approved_on,
              document_reference: form.document_reference.trim(),
              document_revision: form.document_revision.trim(),
              effective_from: kind === 'approval' ? fromLocalInput(form.effective_from) : null,
              rules_digest: digest.trim(),
              evidence_location: form.evidence_location.trim() || null,
              notes: form.notes.trim() || null,
              declaration_confirmed: form.declaration_confirmed,
            })
            .catch(() => null)
        }}
      >
        {kind === 'review' ? 'Record review' : 'Record approval'}
      </Button>
      <CommandResultBanner error={cmd.error} />
    </div>
  )
}

function Activate({ v, view }: { v: AdminVersion; view: PolicyAdminView }) {
  const cmd = useCommand('activate_policy_version')
  const [chosen, setChosen] = useState<string[]>([])
  const [from, setFrom] = useState('')
  const [reason, setReason] = useState('')
  const [confirmed, setConfirmed] = useState(false)
  const fromId = useId()
  const reasonId = useId()
  const authorities = view.authorities.filter((a) => a.authority_mode === 'own_issue' && !a.revoked)
  const effective = fromLocalInput(from)
  return (
    <div className="rounded-lg border border-line p-3">
      <p className="text-sm font-semibold text-ink">Activate for new permits</p>
      <p className="mt-0.5 text-xs text-ink-soft">Choose the issuing authorities and the date. Permits already raised keep their version.</p>
      <div className="mt-2 divide-y divide-line rounded-lg border border-line bg-white px-3">
        {authorities.map((a) => (
          <Checkbox
            key={a.id}
            checked={chosen.includes(a.id)}
            onChange={(on) => setChosen((c) => (on ? [...c, a.id] : c.filter((x) => x !== a.id)))}
            label={`${a.site_name}${a.unit_name ? ` - ${a.unit_name}` : ''} (${a.issuer})`}
            hint={
              a.active_version
                ? `Now: ${a.active_version.version_label} from ${fmtDateTime(a.active_version.effective_from)}`
                : 'No version active: no permit can be raised here yet.'
            }
          />
        ))}
      </div>
      <Field label="Effective from" htmlFor={fromId} required>
        <DateTimeInput id={fromId} value={from} onChange={setFrom} />
      </Field>
      <Field label="Reason" htmlFor={reasonId} required>
        <TextInput id={reasonId} value={reason} onChange={(e) => setReason(e.target.value)} />
      </Field>
      <Checkbox checked={confirmed} onChange={setConfirmed} label={ACTIVATION_DECLARATION} />
      <Button
        loading={cmd.isPending}
        disabled={chosen.length === 0 || !effective || !reason.trim() || !confirmed}
        onClick={() =>
          void cmd
            .run({ policy_version_id: v.id, authority_ids: chosen, effective_from: effective as string, reason: reason.trim(), declaration_confirmed: confirmed })
            .catch(() => null)
        }
      >
        Activate
      </Button>
      <CommandResultBanner error={cmd.error} />
    </div>
  )
}
